import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, FileText, Image as ImageIcon, Info, Trash2 } from 'lucide-react';
import { ReactComponent as NewProjectEmptyIllustration } from '../../../assets/svg/project-empty-state.svg';
import { fireAnalytics } from '../../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../../utils/analyticsHelpers.ts';
import styles from './NewProjectFormV2.module.scss';
import { 
  NewProjectFormData, 
  NewProjectImage, 
  NewProjectValidationState 
} from '../../../types/newProject';
import { newProjectServiceUtils } from '../../../services/newProjectService.ts';
import PlacesAutocomplete from '../PlacesAutocomplete/PlacesAutocomplete.tsx';

const isWelcomeAssistedPdf = (file: File): boolean => {
  const t = file.type?.trim().toLowerCase() || ''
  if (t === 'application/pdf') return true
  const n = file.name?.trim().toLowerCase() || ''
  return n.endsWith('.pdf')
}

interface NewProjectFormV2Props {
  projectData: NewProjectFormData;
  projectImages: NewProjectImage[];
  validation: NewProjectValidationState;
  onProjectDataChange: (data: Partial<NewProjectFormData>) => void;
  onImagesChange: (images: NewProjectImage[]) => void;
  isEditing?: boolean;
  isLoading?: boolean;
  onWelcomeStateChange?: (isWelcome: boolean) => void;
  onChooseAssistedPrecarga?: (file: File) => void;
}

const NewProjectFormV2: React.FC<NewProjectFormV2Props> = ({
  projectData,
  projectImages,
  validation,
  onProjectDataChange,
  onImagesChange,
  isEditing = false,
  isLoading = false,
  onWelcomeStateChange,
  onChooseAssistedPrecarga
}) => {
  const assistedPrecargaFileRef = useRef<HTMLInputElement>(null);
  const [localValidation, setLocalValidation] = useState<NewProjectValidationState>(validation);
  const [localImages, setLocalImages] = useState<NewProjectImage[]>(projectImages);
  const [isDragOver, setIsDragOver] = useState(false);
  const [welcomePdfDragOver, setWelcomePdfDragOver] = useState(false);
  const [welcomePdfDropError, setWelcomePdfDropError] = useState<string | null>(null);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [hasEnteredFlow, setHasEnteredFlow] = useState(() => !!(projectData.name || projectData.description || isEditing));

  // Sincronizar validación local con props
  useEffect(() => {
    setLocalValidation(validation);
  }, [validation]);

  useEffect(() => {
    setLocalImages(projectImages);
  }, [projectImages]);

  useEffect(() => {
    if (projectData.name || projectData.description || isEditing) setHasEnteredFlow(true);
  }, [projectData.name, projectData.description, isEditing]);

  const showWelcome = !hasEnteredFlow && !projectData.name && !projectData.description && !isEditing;
  useEffect(() => {
    onWelcomeStateChange?.(showWelcome);
  }, [showWelcome, onWelcomeStateChange]);

  const Tooltip = ({ content, children }: { content: string; children: React.ReactNode }) => (
    <div className={styles.tooltipContainer}>
      {children}
      <div className={styles.tooltipTriggerWrapper}>
        <button type="button" className={styles.tooltipTrigger} aria-label="Más información">
          <Info size={14} />
        </button>
        <div className={styles.tooltip} role="tooltip">
          {content}
        </div>
      </div>
    </div>
  );

  const formatDateForInput = useCallback((dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }, []);

  // Obtener fecha mínima (hoy)
  const getMinDate = useCallback((): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Manejar cambios en inputs
  const handleInputChange = useCallback((field: keyof NewProjectFormData, value: string) => {
    onProjectDataChange({ [field]: value });
    
    // Limpiar error específico cuando el usuario empieza a escribir
    if (localValidation.project[field]) {
      setLocalValidation(prev => ({
        ...prev,
        project: { ...prev.project, [field]: undefined }
      }));
    }
  }, [onProjectDataChange, localValidation.project]);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setFileUploadError(null);
    const newImages: NewProjectImage[] = [];
    const errors: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const sizeOk = newProjectServiceUtils.validateFileSize(file, 10 * 1024 * 1024);
      const typeOk = newProjectServiceUtils.validateFileType(file, ['image/jpeg', 'image/png', 'image/webp']);
      
      if (!sizeOk) {
        errors.push('El archivo es demasiado grande (máximo 10MB)');
        continue;
      }
      if (!typeOk) {
        errors.push('Tipo de archivo no permitido. Usá JPG, PNG o WebP.');
        continue;
      }
      
      const preview = await newProjectServiceUtils.createImagePreview(file);
      const newImage: NewProjectImage = {
        id: `temp_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview,
        isNew: true
      };
      newImages.push(newImage);
    }
    
    if (errors.length > 0) {
      const message = errors.length === 1
        ? errors[0]
        : `Algunos archivos no se pudieron agregar. Máximo 10MB, formatos: JPG, PNG o WebP.`;
      setFileUploadError(message);
    }
    
    if (newImages.length > 0) {
      const updatedImages = [...localImages, ...newImages];
      setLocalImages(updatedImages);
      onImagesChange(updatedImages);
      if (errors.length === 0) setFileUploadError(null);
    }
    
    event.target.value = '';
  }, [localImages, onImagesChange]);

  // Eliminar imagen
  const handleRemoveImage = useCallback((imageId: string) => {
    const updatedImages = localImages.filter(img => img.id !== imageId);
    
    // Actualizar estado local primero
    setLocalImages(updatedImages);
    
    // Luego notificar al padre
    onImagesChange(updatedImages);
  }, [localImages, onImagesChange]);

  // Drag & Drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Simular el evento de input file
      const mockEvent = {
        target: { files, value: '' }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      handleFileSelect(mockEvent);
    }
  }, [handleFileSelect]);

  const deliverAssistedPrecargaPdf = useCallback(
    (f: File | null | undefined) => {
      if (!f || !onChooseAssistedPrecarga) return;
      if (!isWelcomeAssistedPdf(f)) {
        setWelcomePdfDropError('Solo se admite un archivo PDF.');
        return;
      }
      setWelcomePdfDropError(null);
      fireAnalytics("new_project_empty_state_cta_click", {
        category: "private_projects",
        action: "empty_state_assisted_precarga",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
      });
      onChooseAssistedPrecarga(f);
    },
    [onChooseAssistedPrecarga]
  );

  const handleWelcomePdfDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setWelcomePdfDragOver(true);
  }, []);

  const handleWelcomePdfDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setWelcomePdfDragOver(false);
  }, []);

  const handleWelcomePdfDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setWelcomePdfDragOver(false);
      const f = e.dataTransfer.files?.[0];
      deliverAssistedPrecargaPdf(f);
    },
    [deliverAssistedPrecargaPdf]
  );

  // Validar fechas en tiempo real
  const validateDates = useCallback(() => {
    if (projectData.startDate && projectData.endDate) {
      const isValid = newProjectServiceUtils.validateDateRange(projectData.startDate, projectData.endDate);
      if (!isValid) {
        setLocalValidation(prev => ({
          ...prev,
          project: { ...prev.project, endDate: 'La fecha de finalización debe ser posterior a la fecha de inicio' }
        }));
      }
    }
  }, [projectData.startDate, projectData.endDate]);

  // Efecto para validar fechas
  useEffect(() => {
    validateDates();
  }, [validateDates]);

  return (
    <div className={styles.formContainer}>
      <motion.div
        className={styles.form}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {showWelcome ? (
          <div className={styles.welcomeBlock}>
            <div className={styles.welcomeLayout}>
              <NewProjectEmptyIllustration className={styles.welcomeIllustration} aria-hidden />
              <div className={styles.welcomeContent}>
                <h2 className={styles.welcomeTitle}>Creá tu proyecto</h2>
                <p className={styles.welcomeText}>
                  Elegí cómo arrancar: a mano o con un PDF para precargar borrador y rubros. Después podés recibir
                  presupuestos de proveedores en tu zona.
                </p>
                <div
                  className={
                    onChooseAssistedPrecarga
                      ? `${styles.welcomeChoices} ${styles.welcomeChoicesSplit}`
                      : styles.welcomeChoices
                  }
                >
                  <div className={styles.welcomeChoiceCol}>
                    <h3 className={styles.welcomeChoiceLabel}>Paso a paso</h3>
                    <p className={styles.welcomeChoiceLead}>Proyecto y rubros sin analizar un archivo.</p>
                    <button
                      type="button"
                      className={styles.welcomeButtonOutline}
                      onClick={() => {
                        fireAnalytics("new_project_empty_state_cta_click", {
                          category: "private_projects",
                          action: "empty_state_begin",
                          session_id: getSessionId(),
                          device_category: getDeviceCategory(),
                        });
                        setHasEnteredFlow(true);
                      }}
                      disabled={isLoading}
                    >
                      Empezar a mano
                    </button>
                  </div>
                  {onChooseAssistedPrecarga ? (
                    <div className={styles.welcomeChoiceCol}>
                      <h3 className={styles.welcomeChoiceLabel}>Desde un PDF</h3>
                      <ul className={styles.welcomeAgentList}>
                        <li>Precarga rubros y reparte la documentación por ítem.</li>
                        <li>Sugiere descripciones del proyecto y de cada rubro.</li>
                        <li>Planillas modelo de cotización donde no haya una en el PDF.</li>
                      </ul>
                      <div
                        className={`${styles.welcomeAgentDrop} ${welcomePdfDragOver ? styles.welcomeAgentDropActive : ''}`}
                        onDragOver={handleWelcomePdfDragOver}
                        onDragLeave={handleWelcomePdfDragLeave}
                        onDrop={handleWelcomePdfDrop}
                      >
                        <input
                          ref={assistedPrecargaFileRef}
                          type="file"
                          accept="application/pdf,.pdf"
                          className={styles.welcomeAssistedFileInput}
                          tabIndex={-1}
                          aria-hidden
                          onChange={(ev) => {
                            const f = ev.target.files?.[0] ?? null;
                            ev.target.value = '';
                            deliverAssistedPrecargaPdf(f ?? undefined);
                          }}
                        />
                        <FileText size={28} strokeWidth={1.75} className={styles.welcomeAgentDropIcon} aria-hidden />
                        <button
                          type="button"
                          className={styles.welcomeButton}
                          onClick={() => assistedPrecargaFileRef.current?.click()}
                          disabled={isLoading}
                        >
                          Seleccionar PDF
                        </button>
                        <span className={styles.welcomeAgentDropHint}>O soltá el PDF en esta zona</span>
                        {welcomePdfDropError ? (
                          <p className={styles.welcomePdfDropError} role="alert">
                            {welcomePdfDropError}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Datos del proyecto</h2>
              <p className={styles.formSubtitle}>
                {isEditing
                  ? 'Modificá los datos de tu proyecto'
                  : 'Completá la información para comenzar a recibir cotizaciones'
                }
              </p>
            </div>
            <div className={styles.formBody}>
              <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Identificación</h3>
                <div className={styles.formFields}>
                  <div className={styles.nameLocationFields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="projectName">
                <FileText size={18} />
                Nombre del Proyecto *
              </label>
              <input
                id="projectName"
                type="text"
                className={`${styles.input} ${localValidation.project.name ? styles.inputError : ''}`}
                value={projectData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ej: Construcción de casa familiar"
                disabled={isLoading}
              />
              {localValidation.project.name && (
                <span className={styles.errorText}>{localValidation.project.name}</span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <PlacesAutocomplete
                value={projectData.zone}
                onChange={(value) => handleInputChange('zone', value)}
                placeholder="Ej: Palermo, CABA"
                error={localValidation.project.zone}
                disabled={isLoading}
              />
            </div>
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Fechas</h3>
                <div className={styles.formFields}>
                  <div className={styles.dateFields}>
            <div className={styles.fieldGroup}>
              <Tooltip content="Fecha en que tu proyecto comienza a publicarse en Edify. Durante este período podés agregar rubros para recibir cotizaciones; cada rubro tiene sus propias fechas de presupuesto. No es el inicio de la obra; esta fecha es editable y podés extenderla.">
                <label className={styles.label} htmlFor="startDate">
                  <Calendar size={18} />
                  Fecha de Inicio *
                </label>
              </Tooltip>
              <input
                id="startDate"
                type="date"
                className={`${styles.input} ${localValidation.project.startDate ? styles.inputError : ''}`}
                value={formatDateForInput(projectData.startDate)}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                min={getMinDate()}
                disabled={isLoading}
              />
              {localValidation.project.startDate && (
                <span className={styles.errorText}>{localValidation.project.startDate}</span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <Tooltip content="Fecha hasta la cual tu proyecto permanece publicado en Edify. Hasta entonces podés seguir agregando rubros para recibir cotizaciones; cada rubro tiene sus propias fechas de presupuesto. No es el fin de la obra; podés editarla y extenderla cuando lo necesites.">
                <label className={styles.label} htmlFor="endDate">
                  <Calendar size={18} />
                  Fecha de Finalización *
                </label>
              </Tooltip>
              <input
                id="endDate"
                type="date"
                className={`${styles.input} ${localValidation.project.endDate ? styles.inputError : ''}`}
                value={formatDateForInput(projectData.endDate)}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                min={formatDateForInput(projectData.startDate) || getMinDate()}
                disabled={isLoading}
              />
              {localValidation.project.endDate && (
                <span className={styles.errorText}>{localValidation.project.endDate}</span>
              )}
            </div>
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Descripción</h3>
                <div className={styles.formFields}>
                  <div className={styles.fieldGroup}>
            <label className={styles.label} htmlFor="projectDescription">
              <FileText size={18} />
              Descripción del Proyecto *
            </label>
            <textarea
              id="projectDescription"
              className={`${styles.textarea} ${localValidation.project.description ? styles.inputError : ''}`}
              value={projectData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe detalladamente tu proyecto, incluyendo características especiales, materiales preferidos, etc."
              rows={4}
              disabled={isLoading}
            />
            {localValidation.project.description && (
              <span className={styles.errorText}>{localValidation.project.description}</span>
            )}
                  </div>
                </div>
              </div>
              <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Imágenes</h3>
                <div className={styles.formFields}>
                  <div className={styles.fieldGroup}>
            {/* Header con botón (como rubros) */}
            <div className={styles.imageHeader}>
              <div className={styles.imageHeaderContent}>
                <label className={styles.label}>
                  <ImageIcon size={18} />
                  Imágenes del Proyecto *
                </label>
                <p className={styles.imageSubtitle}>
                  Comparte imagenes representativas de tu proyecto
                </p>
              </div>
              
              <button 
                className={styles.addButton}
                onClick={() => document.getElementById('imageUpload')?.click()}
                disabled={isLoading}
              >
                <ImageIcon size={20} />
                Agregar imagen 
              </button>
            </div>
            
            {/* Input de archivos oculto */}
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className={styles.hiddenFileInput}
              id="imageUpload"
              disabled={isLoading}
            />
            
            {/* Estado vacío - Solo emptyState */}
            {localImages.length === 0 && (
              <div 
                className={`${styles.emptyState} ${isDragOver ? styles.dragOver : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <ImageIcon size={48} className={styles.emptyStateIcon} />
                <h3 className={styles.emptyStateTitle}>No hay imágenes cargadas</h3>
                <p className={styles.emptyStateText}>
                  Usa el botón "Agregar imagen" para cargar fotos de tu proyecto
                </p>
                <p className={styles.hintText}>
                  También puedes arrastrar archivos aquí
                </p>
              </div>
            )}

            {/* Estado con imágenes - Grid + botón de agregar */}
            {localImages.length > 0 && (
              <div className={styles.imageSection}>
                <div className={styles.imageList}>
                  {localImages.map((image) => {
                    return (
                    <div key={image.id} className={styles.imageItem}>
                      <img 
                        src={image.preview} 
                        alt="Preview" 
                        className={styles.imagePreview}
                      />
                      <button
                        type="button"
                        className={styles.removeImageButton}
                        onClick={() => handleRemoveImage(image.id!)}
                        disabled={isLoading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    );
                  })}
                  
                  {/* Botón para agregar más imágenes */}
                  <div 
                    className={`${styles.addMoreButton} ${isDragOver ? styles.dragOver : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('imageUpload')?.click()}
                  >
                    <div className={styles.addMoreContent}>
                      <ImageIcon size={24} />
                      <span>Agregar más</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {fileUploadError && (
              <span className={styles.errorText}>{fileUploadError}</span>
            )}
            {localValidation.project.image && (
              <span className={styles.errorText}>{localValidation.project.image}</span>
            )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default NewProjectFormV2;
